class DrawerMenu extends HTMLElement {
  constructor() {
    'use strict';
    super();

    this.el = this;
    this.mainLevel = this.el.querySelector('.drawer-menu__contents');
    this.mainContentPanelAccordion = this.el.querySelector('[data-accordion-item][data-accordion-main-panel-content]');
        this.mainContentPanelCheckbox = this.el.querySelector('#panel-main-content');
    this.isOpen = this.el.classList.contains('is-open') || this.el.hasAttribute('open');
  
    this.init();
    this.initDrawerStateObserver();
  }

  init() {
    // These toggle submenu panel
    this.firstLevelItemsWithChildren = this.el.querySelectorAll('.drawer-menu__item__input');
    this.firstLevelItemsWithChildren.forEach((input) => {
      input.addEventListener('change', this.toggleMenuItem.bind(this));
    });

    // These handle closing of navigation
    this.closeButtons = this.el.querySelectorAll('[data-close]');
    this.closeButtons.forEach((btn) => {
      btn.addEventListener('click', this.closeAllItems.bind(this));
    });

    this.overlay = this.el.querySelector('[data-overlay]');
    this.overlay.addEventListener('click', this.closeAllItems.bind(this));

    // These handle going back to previous panel
    this.secondLevelBackButtons = this.el.querySelectorAll('[data-drawer-submenu-close]');
    this.secondLevelBackButtons.forEach((button) => {
      button.addEventListener('click', this.toggleSubPanel.bind(this));
    });

    // Handle opening of specific subitem from anywhere in the theme
    this.subitemOpeners = document.querySelectorAll('[data-drawer-submenu-open]');
    this.subitemOpeners.forEach((button) => {
      button.addEventListener('click', this.toggleMenuItemFromTheme.bind(this));
    });
  }
  
  closeAllItems(event, skipEl) {
    const checkboxes = this.mainLevel.querySelectorAll('input:checked');
    const isSmallScreen = window.matchMedia('(max-width: 820px)').matches;
  
    checkboxes.forEach((checkbox) => {
      if (skipEl && checkbox === skipEl) {
        return;
      }
      checkbox.checked = false;
      const accordion = checkbox.closest('[data-accordion-item]');
      if (accordion) {
        accordion.classList.remove('is-open');
      }
    });
  
    if (!skipEl) {
      this.mainLevel.classList.remove('has-open-submenu');
    }
    
    // Check if drawer is open and no submenus are open, then open main content panel
    this.updateMainContentPanelState();
  }

  toggleMenuItem(scope) {
    const input = scope.currentTarget || scope;
    if (!input) return;

    this.closeAllItems(scope, input);

    const accordion = input.closest('[data-accordion-item]');
    if (!accordion) {
      return;
    }

    accordion.classList.toggle('is-open');

    // Recompute whether any submenu (excluding the main content panel) is open
    const anySubmenuOpen = Array.from(this.mainLevel.querySelectorAll('input:checked'))
      .some((cb) => cb !== this.mainContentPanelCheckbox);
    if (anySubmenuOpen) {
      this.mainLevel.classList.add('has-open-submenu');
    } else {
      this.mainLevel.classList.remove('has-open-submenu');
    }
  }

  toggleMenuItemFromTheme(scope) {
    let target = false;
    const button = scope.currentTarget || scope;
    const url = button.getAttribute('data-drawer-submenu-open');

    for (const input of this.firstLevelItemsWithChildren) {
      if (input.getAttribute('data-href') === url) {
        target = input;
        break;
      }
    }

    if (!target) return;

    Woolman.ModalsAndDrawers.showModalOrDrawer(this.el.getAttribute('id'));

    const animationFinished = () => {
      this.el.removeEventListener('transitionend', animationFinished);

      const evt = new Event('change');
      target.checked = true;
      target.dispatchEvent(evt);
    };
    this.el.addEventListener('transitionend', animationFinished);
  }

  toggleSubPanel(event) {
    this.closeAllItems();

    const button = event.currentTarget;
    const targetSelector = button.getAttribute('data-drawer-submenu-close');
    if (!targetSelector) {
      return;
    }

    const input = document.getElementById(targetSelector);
    if (!input) {
      return;
    }

    input.focus();
  }
  
  updateMainContentPanelState() {
    // Check if elements exist
    const panel = this.mainContentPanelAccordion;
    const checkbox = this.mainContentPanelCheckbox;
    if (!this.mainLevel || !panel || !checkbox) {
      return;
    }

    // Check if drawer is open and whether any submenu (excluding main content) is open
    const hasOpenSubmenus = Array.from(this.mainLevel.querySelectorAll('input:checked'))
      .some((cb) => cb !== this.mainContentPanelCheckbox);
    const isDrawerOpen = this.el.classList.contains('is-open') || this.el.hasAttribute('open');

    // If drawer is open but no submenus are open, show main content panel
    if (isDrawerOpen && !hasOpenSubmenus) {
      panel.classList.add('is-open');
      checkbox.checked = true;
      // Ensure styling reflects that no submenu is open
      this.mainLevel.classList.remove('has-open-submenu');
    } else if (hasOpenSubmenus) {
      // If submenus are open, ensure main content panel is closed
      panel.classList.remove('is-open');
      checkbox.checked = false;
      this.mainLevel.classList.add('has-open-submenu');
    }
  }
  
  initDrawerStateObserver() {
    // Create observer to watch for changes to drawer state
    this.drawerObserver = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      
      mutations.forEach((mutation) => {
        // Check if classes changed
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const newIsOpen = this.el.classList.contains('is-open');
          if (newIsOpen !== this.isOpen) {
            this.isOpen = newIsOpen;
            shouldUpdate = true;
          }
        }
        
        // Check if open attribute changed
        if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
          const newIsOpen = this.el.hasAttribute('open');
          if (newIsOpen !== this.isOpen) {
            this.isOpen = newIsOpen;
            shouldUpdate = true;
          }
        }
      });
      
      // If state changed, update the main content panel
      if (shouldUpdate) {
        this.updateMainContentPanelState();
      }
    });
    
    // Start observing the drawer element
    this.drawerObserver.observe(this.el, {
      attributes: true,
      attributeFilter: ['class', 'open']
    });
  }
  
  // Clean up observer when element is removed
  disconnectedCallback() {
    if (this.drawerObserver) {
      this.drawerObserver.disconnect();
    }
  }
}

if (customElements.get('drawer-menu') === undefined) {
  customElements.define('drawer-menu', DrawerMenu);
}
